import React, { useRef, useState } from "react";

import { API } from "aws-amplify";
import { sanitizeOutbound } from "../../../graphql/outbound/outboundCRUD";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { Confirm, SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { getCustomFieldsToSave } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import {
  FilePaths,
  NoteType,
  OutboundStatus,
  VALIDATION_MSG,
} from "../../../utils/constants";
import { saveRemoveFiles } from "../../../utils/fileUtils";
import { isUserFacingError } from "../../../utils/lambdaErrors";
import {
  clearShipWithMemberCache,
  isLinked,
  unlinkOutbound,
} from "../../../utils/shipWith";
import {
  getLogState,
  historyAutoGen,
  normalizeValue,
} from "../../../utils/utils";
import * as utils from "./OutboundUtils";
import { ApproveIcon, RejectIcon } from "../../../components/common/iconography";
import { emailAdviceApprovedHTML } from "./emailAdviceApprovedHTML";
import { emailAdviceRejecteddHTML } from "./emailAdviceRejecteddHTML";

/**
 * What the lambda leaves on a load it unlinks (AD-7): no group, and no
 * appointment, since it needs its own truck now. Applied to the form data and
 * to the record copy the save compares against, so the save neither writes the
 * group's shared Timeslot back onto the rejected advice nor derives a delete
 * of it.
 */
const UNLINKED_FIELDS = {
  shipWithGroupId: null,
  shipWithIds: null,
  timeslotId: null,
  scheduledDateTime: null,
  timeslot: null,
};

const useStyles = makeStyles((theme) => ({
  rejectButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      // Reset on mouse devices
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  saveButton: {
    marginRight: theme.spacing(2),
  },
}));

export const AdviceApproveRejectButton = ({
  record,
  basePath,
  loading,
  isSaving,
  setIsSaving,
  isDeleting,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  // Set once the reject's unlink commits. The save after it is a separate
  // call, and if it fails the form keeps the linked record: a retry must not
  // unlink again (the lambda refuses a load no longer linked), and either
  // button's save must carry the unlinked fields.
  const unlinked = useRef(false);

  const formValidate = () => {
    const newFormState = form.getState();
    if (newFormState.invalid) notify(VALIDATION_MSG, "warning");
  };

  const handleValidateReject = () => {
    let tempMsg = null;
    const formData = form.getState().values;

    // check if rejection documented in notes
    const notesMaxIndex = formData.notes ? formData.notes.length - 1 : -1;
    if (
      notesMaxIndex < 0 ||
      (notesMaxIndex >= 0 &&
        (!formData.notes[notesMaxIndex] || formData.notes[notesMaxIndex].date))
    ) {
      tempMsg = "Don't forget to document reason for rejection in the Notes! ";
    }

    if (tempMsg) {
      setMessage(tempMsg);
      setOpen(true);
    } else handleSave();
  };

  const handleDialogClose = () => setOpen(false);

  const handleDialogConfirm = () => handleSave();

  const handleSave = () => {
    formValidate();

    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transformApprove = async (data) => {
    setIsSaving(true);
    data = {
      ...data,
      status: OutboundStatus.PENDING,
    };
    data.history = [
      ...data.history,
      {
        text: `Advice approved | status: ${data.status} ${getLogState(
          data.state,
        )}`,
        by: window.appUser?.fullName,
        date: new Date(),
        type: NoteType.SHIPMENT,
      },
    ];

    return await transform(data);
  };

  const transformReject = async (data) => {
    setIsSaving(true);
    const linked = isLinked(record);
    if (linked && !unlinked.current) {
      // A rejected advice cannot ride on the truck, so it leaves the group
      // first; a refused unlink refuses the reject (§6.7).
      try {
        await unlinkOutbound(record.id, { reloadYardId: record.reloadYardId });
      } catch (err) {
        if (isUserFacingError(err)) notify(err.message, "warning");
        else
          logError(
            "outboundShipment:AdviceApproveRejectButton:unlink",
            err,
            notify,
          );
        setIsSaving(false);
        return Promise.reject();
      }
      unlinked.current = true;
      clearShipWithMemberCache(record.shipWithIds);
    }
    data = {
      ...data,
      status: OutboundStatus.ADVICE_REJECTED,
    };
    data.history = [
      ...data.history,
      {
        text: linked
          ? `Advice rejected. Removed from its ships-together group.`
          : `Advice rejected.`,
        by: window.appUser?.fullName,
        date: new Date(),
        type: NoteType.SHIPMENT,
      },
    ];

    return await transform(data);
  };

  const transform = async (data) => {
    let previous = record;
    if (unlinked.current) {
      previous = { ...record, ...UNLINKED_FIELDS };
      data = { ...data, ...UNLINKED_FIELDS };
    }
    debugger;
    data.transferToAccountId = null;

    data.customFields = getCustomFieldsToSave(
      data.customFields,
      data.account.outboundCustomFields,
    );

    const ignoreList = [
      "status",
      "carrierType",
      "transferToAccoundId",
      "createdAt",
    ];
    const includeArrayList = ["customFields"];
    const includeObjectList = ["shipTo"];
    data.history = historyAutoGen(
      form,
      data,
      ignoreList,
      includeArrayList,
      includeObjectList,
    );

    data.poNumber = data.poNumber ? data.poNumber : "";
    data.consigneePoNumber = data.consigneePoNumber
      ? data.consigneePoNumber
      : "";
    data.pickupNumber = data.pickupNumber ? data.pickupNumber : "";

    data.normalizedPoNumber = normalizeValue(data.poNumber);
    data.normalizedConsigneePoNumber = normalizeValue(data.consigneePoNumber);
    data.normalizedPickupNumber = normalizeValue(data.pickupNumber);

    if (data.shipTo?.state) data.shipTo.state = data.shipTo.state.toUpperCase();
    if (data.shipTo?.zipCode)
      data.shipTo.zipCode = data.shipTo.zipCode.toUpperCase();

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    try {
      const isAdvance = false;
      const isRollback = false;
      const response = await utils.updateLambdaOutbound(
        previous,
        data,
        isAdvance,
        isRollback,
      );
      if (response.error) throw response.error;

      await Promise.all([
        saveRemoveFiles(
          record,
          data,
          "attachments",
          `${FilePaths.OUTBOUND}/${Date.now()}`,
        ),
      ]);
    } catch (err) {
      logError(
        "outboundShipment:AdviceApproveRejectButton:transform",
        err,
        notify,
        redirect,
      );
      setIsSaving(false);
      return Promise.reject();
    }

    debugger;
    data = sanitizeOutbound(data);

    // clear extra form fields
    delete data.customFieldSelect;
    delete data.saveAddress;

    // clear inventorySearchDialog filters
    delete data.lotType;
    delete data.searchArea;
    delete data.searchTerm;
    delete data.showAll;

    return data;
  };

  const onSuccess = ({ data }) => {
    debugger;
    let emailContent;
    let subject;

    if (data.status === OutboundStatus.PENDING) {
      subject = "Outbound Advice Approved";
      emailContent = emailAdviceApprovedHTML(
        window.appUser.firstName,
        window.appUser.email,
        window.appUser.reloadYard,
        data,
      );
    } else {
      subject = "Outbound Advice Rejected";
      emailContent = emailAdviceRejecteddHTML(
        window.appUser.firstName,
        window.appUser.email,
        window.appUser.reloadYard,
        data,
      );
    }

    API.post("transREST", "/sendEmail", {
      body: {
        to: data.advisedBy,
        subject: subject,
        template: "emailTemplate.html",
        htmlContent: emailContent,
      },
    });

    notify("Advice updated successfully", "success");
    redirect(basePath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError(
      "outboundShipment:AdviceApproveRejectButton:onFailure",
      error,
      notify,
    );
  };

  return (
    <div>
      <SaveButton
        {...props}
        label="Approve"
        className={classes.saveButton}
        submitOnEnter={false}
        undoable={false}
        disabled={isDeleting}
        icon={<ApproveIcon />}
        saving={isSaving}
        handleSubmitWithRedirect={handleSave}
        transform={transformApprove}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <SaveButton
        {...props}
        label="Reject"
        variant="outlined"
        color="default"
        className={classnames("ra-save-button", classes.rejectButton)}
        submitOnEnter={false}
        undoable={false}
        disabled={isDeleting || loading}
        icon={<RejectIcon />}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateReject}
        transform={transformReject}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
      <Confirm
        isOpen={open}
        loading={isSaving}
        title="Save Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </div>
  );
};
